import React, { useState, useRef, useEffect } from "react";

interface Option<T extends number | string = number> {
  value: T;
  label: string;
}

interface SearchSelectProps<T extends number | string = number> {
  options: Option<T>[];
  value: T;
  onChange: (value: T) => void;
  emptyValue?: T;
  placeholder?: string;
  style?: React.CSSProperties;
}

export function SearchSelect<T extends number | string = number>({
  options,
  value,
  onChange,
  emptyValue,
  placeholder = "-- Select --",
  style,
}: SearchSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const empty = emptyValue !== undefined ? emptyValue : (0 as T);
  const selected = options.find((o) => o.value === value);

  const filtered = search
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div ref={containerRef} className="search-select" style={style}>
      <button
        type="button"
        className="search-select-trigger"
        onClick={() => {
          setOpen(!open);
          setSearch("");
        }}
      >
        <span style={{ opacity: selected ? 1 : 0.6 }}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="search-select-arrow">&#9662;</span>
      </button>
      {open && (
        <div className="search-select-dropdown">
          <input
            ref={inputRef}
            type="text"
            className="search-select-input"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="search-select-options">
            <div
              className={`search-select-option ${value === empty ? "selected" : ""}`}
              onClick={() => {
                onChange(empty);
                setOpen(false);
                setSearch("");
              }}
            >
              {placeholder}
            </div>
            {filtered.map((o) => (
              <div
                key={String(o.value)}
                className={`search-select-option ${o.value === value ? "selected" : ""}`}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                  setSearch("");
                }}
              >
                {o.label}
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="search-select-option" style={{ opacity: 0.5 }}>
                No results
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
